import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import type { Todo } from "@acto/taskpane/core/domain";
import { useViewer } from "@acto/taskpane/core/contexts/ViewerContext";
import { SummaryText } from "@acto/taskpane/field-sales-2/components/primitives/SummaryText";
import { RecapSummaryTodoList } from "./RecapSummaryTodoList";

// The AI summary, in the same glass-card visual the creation flow's summary screen uses.
const SummaryCard = styled("div")(({ theme }) => ({
  width: "100%",
  boxSizing: "border-box",
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[24]}`,
  background: "#f8f8f7",
  border: "1px solid rgba(255, 255, 255, 0.64)",
  borderRadius: theme.fieldSalesTwo.radius[32],
}));

interface RecapReadViewProps {
  /** The AI summary text. When empty/null the summary card is omitted. */
  summary?: string | null;
  /** Note-keyed todos to list. */
  todos: Todo[];
  /** Ids animating out via the check-off deletion animation (creation flow only). */
  checkingOffIds?: string[];
  /** Ids checked-in-place in the open bucket during the undo window (pending completion). */
  checkedTodoIds?: string[];
  /** Ids to show under the "Done" bucket regardless of server state (committed completion overlay). */
  completedTodoIds?: string[];
  /** Open a todo for editing. Omit for a read-only (non-interactive) view. */
  onSelectTodo?: (id: string) => void;
  /** Complete a todo by tapping its checkbox. Omit in the creation flow (non-completable there). */
  onCompleteTodo?: (id: string) => void;
  /** Accessible label for the completable checkbox button. */
  completeTodoLabel?: string;
  /**
   * Split open todos into "Assigned to me" / "Unassigned" / "Assigned to others", locking the
   * others' bucket (read-only — a todo assigned to another person can't be completed or edited).
   * Off by default so the creation flow keeps a single open bucket. Recap todos are created
   * unassigned, so "Unassigned" is a real, distinct bucket — not the same as "mine".
   */
  groupByAssignee?: boolean;
}

/**
 * Presentational read-only core of a recap: the AI summary card + the note-keyed todos, split into
 * an open ("Aufgaben") and a done ("Erledigt") bucket so everything attached to the recap stays
 * visible after completion. Holds no data fetching — the caller supplies `summary` and `todos`.
 * Shared by the creation flow's summary screen (passes `onSelectTodo` / `checkingOffIds`; its todos
 * are all open, so the done bucket is empty) and the static view (passes `onCompleteTodo` so open
 * todos are checkable, plus the completion overlay that moves committed todos into the done bucket).
 */
export function RecapReadView({
  summary,
  todos,
  checkingOffIds,
  checkedTodoIds,
  completedTodoIds = [],
  onSelectTodo,
  onCompleteTodo,
  completeTodoLabel,
  groupByAssignee = false,
}: RecapReadViewProps) {
  const t = useTranslations("fieldSalesTwo.recaps");
  const { isViewer } = useViewer();

  const isDone = (todo: Todo) => todo.isCompleted() || completedTodoIds.includes(todo.id);
  const openTodos = todos.filter((todo) => !isDone(todo));
  const doneTodos = todos.filter(isDone);

  const openMine = openTodos.filter((todo) => !!todo.assignee && isViewer(todo.assignee));
  const openUnassigned = openTodos.filter((todo) => !todo.assignee);
  const openOthers = openTodos.filter((todo) => !!todo.assignee && !isViewer(todo.assignee));

  return (
    <>
      {summary && (
        <SummaryCard>
          <SummaryText>{summary}</SummaryText>
        </SummaryCard>
      )}

      {groupByAssignee ? (
        <>
          {/* "Mine" and "Unassigned" are actionable (complete + edit); "Others" is locked. Empty
              buckets render nothing (RecapSummaryTodoList returns null). */}
          <RecapSummaryTodoList
            heading={t("summary.assignedToMe")}
            todos={openMine}
            checkingOffIds={checkingOffIds}
            checkedIds={checkedTodoIds}
            onSelect={onSelectTodo}
            onComplete={onCompleteTodo}
            completeLabel={completeTodoLabel}
          />
          <RecapSummaryTodoList
            heading={t("summary.unassigned")}
            todos={openUnassigned}
            checkingOffIds={checkingOffIds}
            checkedIds={checkedTodoIds}
            onSelect={onSelectTodo}
            onComplete={onCompleteTodo}
            completeLabel={completeTodoLabel}
          />
          <RecapSummaryTodoList heading={t("summary.assignedToOthers")} todos={openOthers} readOnly />
        </>
      ) : (
        <RecapSummaryTodoList
          heading={t("summary.todos")}
          todos={openTodos}
          checkingOffIds={checkingOffIds}
          checkedIds={checkedTodoIds}
          onSelect={onSelectTodo}
          onComplete={onCompleteTodo}
          completeLabel={completeTodoLabel}
        />
      )}

      <RecapSummaryTodoList heading={t("summary.done")} todos={doneTodos} done />
    </>
  );
}
